import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { useErrorLogger } from '../services/frontendErrorLogger';

/**
 * Fallback UI to display when an error occurs
 */
const DefaultErrorFallback = ({ error, resetError }) => (
  <div className="error-boundary-fallback">
    <div className="error-boundary-content">
      <h2>Something went wrong</h2>
      <p>We've encountered an unexpected error. Our team has been notified.</p>
      <div className="error-details">
        <p>{error.message || 'Unknown error'}</p>
      </div>
      {resetError && (
        <button 
          onClick={resetError}
          className="btn btn-primary mt-3"
        >
          Try Again
        </button>
      )}
    </div>
  </div>
);

DefaultErrorFallback.propTypes = {
  error: PropTypes.object.isRequired,
  resetError: PropTypes.func
};

/**
 * Error boundary wrapper to catch errors in child components
 */
class ErrorBoundaryBase extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to our backend
    this.props.logError(error, errorInfo);
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    const { hasError, error } = this.state;
    const { children, FallbackComponent } = this.props;

    if (hasError) {
      const ErrorFallback = FallbackComponent || DefaultErrorFallback;
      return <ErrorFallback error={error} resetError={this.resetError} />;
    }

    return children;
  }
}

ErrorBoundaryBase.propTypes = {
  children: PropTypes.node.isRequired,
  FallbackComponent: PropTypes.elementType,
  logError: PropTypes.func.isRequired
};

/**
 * HOC that connects the ErrorBoundary to the error logger
 */
export const ErrorBoundary = ({ children, FallbackComponent }) => {
  const { errorBoundaryHandler } = useErrorLogger();
  
  return (
    <ErrorBoundaryBase 
      logError={errorBoundaryHandler}
      FallbackComponent={FallbackComponent}
    >
      {children}
    </ErrorBoundaryBase>
  );
};

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  FallbackComponent: PropTypes.elementType
};

export default ErrorBoundary;
